import { useMemo } from 'react'

import { convertAllowancesToAllowanceParams, getContributionPercentagesForDynamicAllowance, getDynamicAllowance, isDynamicAllowances, isQuotingQuoteAllowanceArray } from 'apps/employer_renewals/pages/allowances/utils'
import useFlatAllowanceCalculations from 'components/hooks/quotes/useFlatAllowanceCalculations'
import { useQuoteCosts } from 'components/hooks/quotes/useQuoteCosts'
import useQuoteResultCosts from 'components/hooks/quotes/useQuoteResultCosts'
import { AllowanceStrategies } from 'pages/employer_onboarding/quote/types'
import { type PostQuotingProspectsProspectIdQuotesQuoteIdAllowancesAllowancesItem as UpdateAllowanceItem, type QuotingQuoteAllowance, type QuotingQuoteResult } from 'utils/web_api.schemas'

type Props = {
  allowances: QuotingQuoteAllowance[] | UpdateAllowanceItem[]
  quoteResults: QuotingQuoteResult[]
}

export default function useRenewalQuoteCosts ({ allowances, quoteResults }: Props) {
  const isUsingDynamicAllowances = useMemo(() => {
    return isDynamicAllowances(allowances)
  }, [allowances])

  const dynamicAllowance = useMemo(() => {
    return getDynamicAllowance(allowances)
  }, [allowances])

  const flatAllowances: UpdateAllowanceItem[] = useMemo(() => {
    if (isQuotingQuoteAllowanceArray(allowances)) {
      return convertAllowancesToAllowanceParams(allowances)
    }

    return allowances
  }, [allowances])

  const flatAllowanceCalculations = useFlatAllowanceCalculations(quoteResults, flatAllowances)

  const contributionPercentages = getContributionPercentagesForDynamicAllowance(dynamicAllowance)

  const dynamicAllowanceCosts = useQuoteResultCosts(
    quoteResults,
    contributionPercentages.employee,
    contributionPercentages.spouse,
    contributionPercentages.dependent
  )

  const { employerPortionOfInsurance } = useQuoteCosts(
    quoteResults,
    flatAllowanceCalculations,
    dynamicAllowanceCosts,
    isUsingDynamicAllowances ? AllowanceStrategies.Smart : AllowanceStrategies.Flat
  )

  return {
    employerCost: employerPortionOfInsurance,
    flatAllowanceCalculations,
    dynamicAllowanceCosts,
  }
}
